import { CHANNELS, CHANNEL_LABELS, STAGES, type BucketField } from '@joker/domain'

/**
 * What the cohort editor knows about each rule field: a label people
 * recognise, and what kind of value it takes. The rule language itself lives
 * in shared/domain; this is only how the screen presents it.
 */

export type FieldKind = 'boolean' | 'number' | 'choice' | 'text'

export interface FieldInfo {
  label: string
  kind: FieldKind
  /** For choice fields: the values, with labels. */
  choices?: Array<{ value: string; label: string }>
  /** Shown on screen in units of 1/scale of the stored value: paidCents is entered in dollars. */
  scale?: number
  hint?: string
}

export const FIELD_INFO: Record<BucketField, FieldInfo> = {
  stage: { label: 'Stage', kind: 'choice', choices: STAGES.map((s) => ({ value: s.id, label: s.label })) },
  channel: { label: 'Channel', kind: 'choice', choices: CHANNELS.map((c) => ({ value: c, label: CHANNEL_LABELS[c] })) },
  practiceId: { label: 'Practice (id)', kind: 'text' },
  sessionsTotal: { label: 'Training sessions', kind: 'number' },
  daysSincePaid: { label: 'Days since paying', kind: 'number' },
  daysSinceLastSession: { label: 'Days since last session', kind: 'number' },
  daysSinceSignup: { label: 'Days since signing up', kind: 'number' },
  emailOpens30d: { label: 'Emails opened, last 30 days', kind: 'number' },
  hasPhone: { label: 'Has a phone number', kind: 'boolean' },
  hasEmailConsent: { label: 'Agreed to email', kind: 'boolean' },
  rx: { label: 'Reading prescription', kind: 'text' },
  ageRange: { label: 'Age range', kind: 'text' },
  essentialsSessions: { label: 'Essentials programme length (sessions)', kind: 'number' },
  essentialsClass: { label: 'Essentials class', kind: 'text' },
  essentialsRemaining: { label: 'Essentials sessions left', kind: 'number' },
  essentialsProgress: { label: 'Essentials progress', kind: 'number', hint: '0.5 is half the programme, 1 the whole' },
  isTest: { label: 'Test account', kind: 'boolean' },
  isSuperseded: { label: 'Replaced by a newer account', kind: 'boolean' },
  isEmployee: { label: 'Employee', kind: 'boolean' },
  isOldEnrolment: { label: 'Old enrolment', kind: 'boolean' },
  isSpam: { label: 'Spam', kind: 'boolean' },
  isVip: { label: 'VIP', kind: 'boolean' },
  isOptometrist: { label: 'Is an optometrist', kind: 'boolean' },
  tabletReceived: { label: 'Tablet sent out', kind: 'boolean' },
  tabletReturned: { label: 'Tablet returned', kind: 'boolean' },
  paymentChannel: {
    label: 'Paid through',
    kind: 'choice',
    choices: ['wix', 'quickbooks', 'wire', 'shopify', 'optometrist'].map((v) => ({ value: v, label: v })),
  },
  paidCents: { label: 'Money paid ($)', kind: 'number', scale: 100 },
  optometristUserType: {
    label: "Practice patient's type",
    kind: 'choice',
    choices: ['test', 'team', 'paying'].map((v) => ({ value: v, label: v })),
  },
  examinedBy: { label: 'Eye check done by', kind: 'text' },
  hasTestOrder: { label: 'Has a $1 test order', kind: 'boolean' },
  signupSource: { label: 'Signed up from', kind: 'text' },
  signupPromo: { label: 'Sign-up promo', kind: 'text' },
}

export type FieldOp = 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'isNull' | 'notNull'

export const OPS_FOR: Record<FieldKind, Array<{ op: FieldOp; label: string }>> = {
  boolean: [{ op: 'eq', label: 'is' }],
  number: [
    { op: 'gte', label: 'at least' },
    { op: 'lte', label: 'at most' },
    { op: 'gt', label: 'more than' },
    { op: 'lt', label: 'less than' },
    { op: 'eq', label: 'exactly' },
    { op: 'isNull', label: 'is unknown' },
    { op: 'notNull', label: 'is known' },
  ],
  choice: [
    { op: 'eq', label: 'is' },
    { op: 'neq', label: 'is not' },
    { op: 'isNull', label: 'is empty' },
    { op: 'notNull', label: 'is set' },
  ],
  text: [
    { op: 'eq', label: 'is' },
    { op: 'neq', label: 'is not' },
    { op: 'isNull', label: 'is empty' },
    { op: 'notNull', label: 'is set' },
  ],
}

export const opLabel = (kind: FieldKind, op: FieldOp) => OPS_FOR[kind].find((o) => o.op === op)?.label ?? op
